import { Context, EnrollmentStatus } from '@/pipeline/stateMachine';
import { AnyEventObject, StateConfig } from 'xstate';
import gql from 'graphql-tag';
import { SchoolCategory } from '@/helpers/schoolCategories';
import { Provider } from '@/helpers/accountsIframe';

interface Payment {
  id?: number;
  lastDigits?: string;
  paymentMethod?: string;
  paymentStatus?: string;
  isRecurrentCreditCard?: boolean;
  backofficeRecurrentCardId?: number;
}

interface Document {
  id?: number;
  url?: string;
  citadelId?: number;
  documentType?: string;
}

export interface GetEnrollment {
  id?: number;
  areDocumentsSent?: boolean;
  isContractAccepted?: boolean;
  enrollmentStatus?: EnrollmentStatus;
  partnerCode?: string;
  currentPayment?: Payment;
  payment?: Payment[];
  backofficeStudentAcademicRecord?: string;
  student?: {
    id?: number;
    cpf?: string;
    rg?: string;
    rgIssuer?: string;
    rgIssueDate?: string;
    rgIssueState?: string;
    nationality?: string;
    placeOfBirth?: string;
    birthDate?: string;
    race?: string;
    phone?: string;
    email?: string;
    gender?: string;
    fullName?: string;
    schoolCategory?: SchoolCategory;
    schoolFormationYear?: number;
    schoolCity?: string;
    householdIncome?: string;
    motherName?: string;
    fatherName?: string;
    documents?: Document[];
    cityBirth?: string;
    address?: {
      id?: number;
      complement?: string;
      neighborhood?: string;
      state?: string;
      streetName?: string;
      streetNumber?: string;
      zipCode?: string;
      city?: string;
    };
    signupMethod?: string;
  };
  financialSponsor?: {
    id?: number;
    cpf?: string;
    rg?: string;
    rgIssuer?: string;
    rgIssueDate?: string;
    rgIssueState?: string;
    nationality?: string;
    placeOfBirth?: string;
    birthDate?: string;
    race?: string;
    phone?: string;
    email?: string;
    gender?: string;
    fullName?: string;
    documents?: Document[];
    address?: {
      id?: number;
      complement?: string;
      neighborhood?: string;
      state?: string;
      streetName?: string;
      streetNumber?: string;
      zipCode?: string;
      city?: string;
    };
  };
}

export interface GetEnrollmentQueryResult {
  getEnrollment: GetEnrollment;
}

interface GetPipelineRun {
  id: number;
  state: StateConfig<Context, AnyEventObject>;
}

export interface GetPipelineRunQueryResult {
  getPipelineRun: GetPipelineRun;
}

interface GetPaymentData {
  paymentValue: number;
}

export interface GetPaymentDataQueryResult {
  getPaymentData: GetPaymentData;
}

export interface GetContractHtmlQueryResult {
  getEnrollment: { backofficeContractHtml: string };
}

export interface GetUserAccounts {
  email: string;
  provider: Provider[];
}

export interface GetUserAccountsQueryResult {
  getUserAccounts: GetUserAccounts;
}

export interface GetTokenDInfo {
  id: number;
  email: string;
}

export interface GetTokenDInfoQueryResult {
  getTokenDInfo: GetTokenDInfo;
}

export interface GetPixPaymentStatus {
  id: number;
  backofficePaymentId: number;
  paymentMethod?: string;
  paymentStatus?: 'waitingPayment' | 'paid' | 'paymentRefused' | 'none';
  pixCode?: string;
  createdAt: Date;
  updatedAt: Date;
  isPixCodeExpired: boolean;
  lyceumPixPaymentStatus: {
    status: string;
    paymentStatus?: string;
    paymentDate?: Date;
    paymentValue: number;
  };
}
export interface GetPixPaymentStatusResult {
  getPixPaymentStatus: GetPixPaymentStatus;
}

interface AllCities {
  city: string;
  state: string;
}

export interface AllCitiesQueryResult {
  allCities: AllCities[];
}

interface AllCampus {
  id: number;
  name: string;
  neighborhood: string;
  address: string;
}
export interface AllCampusQueryResult {
  allCampus: AllCampus[];
}

interface AllCourses {
  id: number;
  name: string;
  courseType: string;
  durationYears: number;
}

export interface AllCoursesQueryResult {
  allCourses: AllCourses[];
}

export interface IsJacadCourseQueryResult {
  isJacadCourse: boolean;
}

export const enrollmentResultFields = `
  id
  areDocumentsSent
  isContractAccepted
  enrollmentStatus
  partnerCode
  backofficeStudentAcademicRecord
  currentPayment {
    id
    lastDigits
    paymentMethod
    paymentStatus
    isRecurrentCreditCard
    backofficeRecurrentCardId
  }
  payment {
    id
    lastDigits
    paymentMethod
    paymentStatus
    isRecurrentCreditCard
    backofficeRecurrentCardId
  }
  student {
    id
    cpf
    rg
    rgIssuer
    rgIssueDate
    rgIssueState
    nationality
    placeOfBirth
    birthDate
    race
    phone
    email
    gender
    fullName
    schoolCategory
    schoolFormationYear
    schoolCity
    householdIncome
    motherName
    fatherName
    signupMethod
    cityBirth
    documents {
      id
      url
      citadelId
      documentType
    }
    address {
      id
      complement
      neighborhood
      state
      streetName
      streetNumber
      zipCode
    }
  }
  financialSponsor {
    id
    cpf
    rg
    rgIssuer
    rgIssueDate
    rgIssueState
    nationality
    placeOfBirth
    birthDate
    race
    phone
    email
    gender
    fullName
    documents {
      id
      url
      citadelId
      documentType
    }
    address {
      id
      complement
      neighborhood
      state
      streetName
      streetNumber
      zipCode
    }
  }
`;

export const getEnrollment = gql`
  query getEnrollment {
    getEnrollment {
      ${enrollmentResultFields}
    }
  }
`;

export const getPipelineRun = gql`
  query getPipelineRun($enrollmentId: Float!) {
    getPipelineRun(enrollmentId: $enrollmentId) {
      id
      state
    }
  }
`;

export const getPaymentData = gql`
  query getPaymentData {
    getPaymentData {
      paymentValue
    }
  }
`;

export const getContractHtml = gql`
  query getContract {
    getEnrollment {
      backofficeContractHtml
    }
  }
`;

export const getUserAccounts = gql`
  query {
    getUserAccounts {
      email
      provider
    }
  }
`;

export const getTokenDInfo = gql`
  query getTokenDInfo($tokenD: String!) {
    getTokenDInfo(tokenD: $tokenD) {
      id
      email
    }
  }
`;

export const getPixPaymentStatus = gql`
  query getPixPaymentStatus {
    getPixPaymentStatus {
      id
      backofficePaymentId
      paymentMethod
      paymentStatus
      pixCode
      createdAt
      updatedAt
      isPixCodeExpired
      lyceumPixPaymentStatus {
        status
        paymentStatus
        paymentDate
        paymentValue
      }
    }
  }
`;

export const allCities = gql`
  query allCities($take: Float, $skip: Float) {
    allCities(take: $take, skip: $skip) {
      city
      state
    }
  }
`;

export const allCampus = gql`
  query allCampus(
    $city: String
    $state: String
    $take: Float
    $skip: Float
    $isAvailable: Boolean
  ) {
    allCampus(city: $city, state: $state, take: $take, skip: $skip, isAvailable: $isAvailable) {
      id
      name
      neighborhood
      address
    }
  }
`;

export const isJacadCourse = gql`
  query isJacadCourse {
    isJacadCourse
  }
`;

export const allCourses = gql`
  query allCourses($take: Float, $skip: Float, $isAvailable: Boolean) {
    allCourses(take: $take, skip: $skip, isAvailable: $isAvailable) {
      id
      name
      courseType
      durationYears
    }
  }
`;
